import { useEffect, useRef, useState } from "react";
import { useT } from "../i18n/index.js";
import { Button } from "../ui/Button.js";
import { audio } from "./engine.js";
import { AsyncToggle } from "../ui/AsyncToggle.js";

interface Levels { sound: number; music: number }

/** Sliders preview locally while dragged and commit one server settings patch on release. */
export function AudioControls({ settings, save }: { settings: Levels & { backgroundMusic: boolean }; save: (patch: Partial<Levels & { backgroundMusic: boolean }>) => Promise<void> }) {
  const t = useT();
  const [levels, setLevels] = useState<Levels>({ sound: settings.sound, music: settings.music });
  const restore = useRef<Levels>({ sound: settings.sound || 0.7, music: settings.music || 0.35 });
  const pending = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef(levels);
  useEffect(() => {
    const next = { sound: settings.sound, music: settings.music };
    setLevels(next); latest.current = next;
  }, [settings.sound, settings.music]);
  useEffect(() => () => { if (pending.current) clearTimeout(pending.current); }, []);
  const commit = () => {
    if (pending.current) clearTimeout(pending.current);
    pending.current = null;
    void save(latest.current);
  };
  const preview = (channel: keyof Levels, value: number) => {
    const next = { ...latest.current, [channel]: value };
    latest.current = next; setLevels(next); audio.configure(next);
    if (value > 0) restore.current[channel] = value;
    if (pending.current) clearTimeout(pending.current);
    pending.current = setTimeout(commit, 350);
  };
  const toggle = (channel: keyof Levels) => {
    preview(channel, latest.current[channel] > 0 ? 0 : restore.current[channel]);
    commit();
  };
  return (
    <div data-testid="audio-controls" style={{ display: "grid", gap: 14 }}>
      <p className="hint" style={{ margin: 0 }}>{t("settings.audio.hint")}</p>
      {(["sound", "music"] as const).map((channel) => (
        <div key={channel} className="field" style={{ margin: 0 }}>
          <div className="row" style={{ gap: 10, marginBottom: 5 }}>
            <label htmlFor={`audio-${channel}`} className="grow">{t(`settings.audio.${channel}`)}</label>
            <output htmlFor={`audio-${channel}`} className="mono">{Math.round(levels[channel] * 100)}%</output>
            <Button variant="ghost" aria-pressed={levels[channel] === 0} aria-label={t(`settings.audio.mute.${channel}`)} data-testid={`audio-mute-${channel}`} onClick={() => toggle(channel)} style={{ minHeight: 36 }}>
              {levels[channel] === 0 ? t("settings.audio.unmute") : t("settings.audio.mute")}
            </Button>
          </div>
          <input id={`audio-${channel}`} data-testid={`audio-${channel}`} type="range" min="0" max="1" step="0.05" value={levels[channel]} aria-valuetext={`${Math.round(levels[channel] * 100)}%`} onChange={(event) => preview(channel, Number(event.target.value))} onPointerUp={commit} onKeyUp={commit} style={{ width: "100%", accentColor: "var(--accent)", minHeight: 28 }} />
        </div>
      ))}
      <AsyncToggle checked={settings.backgroundMusic} save={backgroundMusic => save({ backgroundMusic })} testId="audio-background-music"><strong>{t("settings.audio.background")}</strong><span className="hint" style={{ display: "block", marginTop: 4 }}>{t("settings.audio.background.hint")}</span></AsyncToggle>
      <Button onClick={() => audio.play("reward")} data-testid="audio-preview" disabled={levels.sound === 0}>{t("settings.audio.preview")}</Button>
    </div>
  );
}
